import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageShell } from "./PageShell";
import { MethodFlow } from "./MethodFlow";
import { FlowSimulator } from "./FlowSimulator";
import { Decisions } from "./Decisions";
import { FinalCTA } from "./FinalCTA";
import { MaskedLine, Reveal, SectionHead, WHATSAPP_URL, WhatsAppIcon, EASE } from "./shared";
import { SIM_DEFAULTS, calcularCenario } from "./simulatorMath";

const PILLARS = [
  { n: "01", title: "Lote como unidade de gestão", desc: "Cada compra vira uma unidade econômica com custo, margem e resultado próprios — nada se mistura." },
  { n: "02", title: "Margem em atualização contínua", desc: "A cada remarcação e a cada venda, a margem é recalculada. O gestor decide com o número de agora." },
  { n: "03", title: "Projetado × Realizado × Potencial", desc: "Três réguas claras: o que se planejou, o que já aconteceu e o que ainda pode ser capturado." },
  { n: "04", title: "Custos × Margem × Faturamento", desc: "Os três eixos do negócio conectados em uma única visão, do lote ao resultado operacional." },
];

export default function MethodPage() {
  const [sim, setSim] = useState(SIM_DEFAULTS);
  const calc = useMemo(() => calcularCenario(sim), [sim]);

  return (
    <PageShell testId="method-page">
      <section data-testid="method-hero" className="relative overflow-hidden pt-[72px]">
        <div className="hero-grid pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute left-1/2 top-[-260px] h-[520px] w-[760px] -translate-x-1/2 rounded-full bg-tria-vivid/18 blur-[130px]" />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 pb-14 pt-16 text-center sm:px-8 sm:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-tria-sky/25 bg-tria-sky/[0.07] px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-tria-sky" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.22em] text-tria-sky">O método TRIA</span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl" data-testid="method-title">
            <MaskedLine delay={0.25}>Um método que vai</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gradient-blue">além da planilha</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-tria-mist sm:text-lg"
          >
            O fluxo econômico completo do negócio — da compra do lote ao resultado operacional —
            organizado em uma cadeia de indicadores que se atualiza sozinha e se corrige em tempo real.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-9 flex flex-col items-center gap-3.5 sm:flex-row"
          >
            <a
              data-testid="method-cta-demo"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-blue inline-flex items-center gap-2.5 rounded-full bg-tria-vivid px-8 py-4 text-[15px] font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-tria-blue"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Agendar demonstração
            </a>
            <Link
              data-testid="method-cta-platform"
              to="/"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-[15px] font-semibold text-white transition-all duration-300 hover:border-tria-sky/50 hover:bg-white/5"
            >
              Conhecer a plataforma
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="relative py-10 sm:py-14" data-testid="method-flow-section">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <SectionHead
            eyebrow="Fluxo do método"
            testId="method-flow-head"
            title={<>Da compra do lote ao lucro — <span className="text-gradient-blue">um fluxo que se corrige sozinho</span></>}
            sub="Simule com os números do seu negócio: enquanto o ponto de equilíbrio não é atingido, o cenário mostra quanto falta de faturamento. Quando é atingido, a margem de contribuição passa a formar resultado."
          />
          <FlowSimulator sim={sim} setSim={setSim} calc={calc} />
          <MethodFlow sim={calc} />
        </div>
      </section>

      <section className="relative py-16 sm:py-24" data-testid="method-pillars-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead
            eyebrow="Os 4 pilares"
            testId="method-pillars-head"
            title={<>O diferencial está no <span className="text-gradient-blue">método</span></>}
            sub="Quatro princípios que separam a TRIA de planilhas e sistemas genéricos."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div
                  data-testid={`method-pillar-${i + 1}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6 transition-all duration-300 hover:border-tria-blue/40"
                >
                  <span className="pointer-events-none absolute -right-3 -top-5 font-display text-[88px] font-bold leading-none text-white/[0.045] transition-colors duration-300 group-hover:text-tria-blue/10">
                    {p.n}
                  </span>
                  <span className="font-mono text-[10px] font-bold tracking-[0.25em] text-tria-sky">PILAR {p.n}</span>
                  <h3 className="mt-3 font-display text-[17px] font-semibold leading-snug text-white">{p.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-tria-mist">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Decisions />
      <FinalCTA />
    </PageShell>
  );
}
